// slightly modified version of: https://github.com/mParticle/mparticle-web-sdk/blob/master/snippet.js

declare global {
  interface Window {
    mParticle: any;
  }
}

// stubbing function
// pushes an array of 2 arguments into readyQueue: 1. the method, and 2. the arguments passed to the method
// if the method is on the eCommerce or identity object, then the 1st argument is base conatenated with "." and the method name
// ie: Identity.login, eCommerce.setCurrencyCode
// in main.js, the function "processPreloadedItem" will parse and run stubbed methods stored in the readyQueue (config.rq)
function preloadMethod(method: string, base?: string) {
  return function () {
    if (base) {
      method = `${base}.${method}`;
    }
    // eslint-disable-next-line prefer-rest-params
    const args = Array.prototype.slice.call(arguments);
    args.unshift(method);
    window.mParticle.config.rq.push(args);
  };
}

export const setUpMParticle = (apiKey: string, releaseEnv: string) => {
  // stub mParticle and major mParticle classes EventType, eCommerce, and Identity to exist before full
  // mParticle object is initialized
  window.mParticle = window.mParticle || {};
  window.mParticle.EventType = {
    Unknown: 0,
    Navigation: 1,
    Location: 2,
    Search: 3,
    Transaction: 4,
    UserContent: 5,
    UserPreference: 6,
    Social: 7,
    Other: 8,
  };
  window.mParticle.eCommerce = { Cart: {} };
  window.mParticle.Identity = {};
  window.mParticle.config = window.mParticle.config || {};
  window.mParticle.config.isDevelopmentMode = releaseEnv !== 'production';
  window.mParticle.config.rq = [];
  window.mParticle.config.snippetVersion = 2.3;
  window.mParticle.ready = function (f) {
    window.mParticle.config.rq.push(f);
  };

  // methods to be stubbed from the main mParticle object, mParticle.eCommerce, and mParticle.Identity
  // methods that return objects are not stubbed
  const mainMethods = [
    'endSession',
    'logError',
    'logBaseEvent',
    'logEvent',
    'logForm',
    'logLink',
    'logPageView',
    'setSessionAttribute',
    'setAppName',
    'setAppVersion',
    'setOptOut',
    'setPosition',
    'startNewSession',
    'startTrackingLocation',
    'stopTrackingLocation',
  ];
  const ecommerceMethods = [
    'createProduct',
    'createTransactionAttributes',
    'logProductAction',
    'setCurrencyCode',
    'logCheckout',
  ];
  const identityMethods = ['getCurrentUser', 'identify', 'login', 'logout', 'modify'];

  // iterates through methods above to create stubs
  for (const method of mainMethods) {
    window.mParticle[method] = preloadMethod(method);
  }
  for (const method of ecommerceMethods) {
    window.mParticle.eCommerce[method] = preloadMethod(method, 'eCommerce');
  }
  for (const method of identityMethods) {
    window.mParticle.Identity[method] = preloadMethod(method, 'Identity');
  }

  // set data planning query parameters
  let dpId: string;
  let dpV: number;
  const {
    config,
    config: { dataPlan },
  } = window.mParticle;
  const env = config.isDevelopmentMode ? 1 : 0;
  let dbUrl = `?env=${env}`;

  if (dataPlan) {
    dpId = dataPlan.planId;
    dpV = dataPlan.planVersion;
    if (dpId) {
      if (dpV && (dpV < 1 || dpV > 1000)) {
        dpV = null;
      }
      dbUrl += `&plan_id='${dpId}${dpV ? `&plan_version=${dpV.toString()}` : ''}`;
    }
  }

  // add mParticle script dynamically to the page, insert before the first script tag
  const mp = document.createElement('script');
  mp.type = 'text/javascript';
  mp.async = true;
  mp.src = `${
    document.location.protocol === 'https:' ? 'https://jssdkcdns' : 'http://jssdkcdn'
  }.mparticle.com/js/v2/${apiKey}/mparticle.js${dbUrl}`;
  const s = document.querySelectorAll('script')[0];
  s.parentNode.insertBefore(mp, s);
};
